import { ref, shallowRef, watch, markRaw, nextTick, type Ref } from 'vue'
import type { NuxtApp } from '#app'
import { useNuxtApp, useRuntimeConfig, defineNuxtPlugin } from '#app'
import { useBrochureRequest } from '~/stores/brochureRequest'
import { logIterableError } from '~/utils/iterable-log'
import { sdkMetadataPlacementIdToMapKey } from '~/utils/iterable-live-merge'

const JWT_FETCH_MAX_ATTEMPTS = 3
const JWT_RETRY_BASE_DELAY_MS = 300

function sleepMs (ms: number): Promise<void> {
  const clampedMs = Math.max(0, ms)
  if (clampedMs === 0) return Promise.resolve()
  return new Promise((resolve) => setTimeout(resolve, clampedMs))
}

async function fetchIterableJwtToken (body: { email?: string; userID?: string }): Promise<string> {
  let lastErr: unknown
  for (let attempt = 0; attempt < JWT_FETCH_MAX_ATTEMPTS; attempt++) {
    try {
      const { jwt_token } = await $fetch<{ jwt_token: string }>('/api/iterable-jwt', {
        method: 'POST',
        body
      })
      return jwt_token
    } catch (e) {
      lastErr = e
      if (attempt < JWT_FETCH_MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, JWT_RETRY_BASE_DELAY_MS * (attempt + 1)))
      }
    }
  }
  throw lastErr
}

/**
 * Safe access to Iterable embedded manager messages (SDK may omit getMessages or throw).
 * Mirrors: window?.itbl?.getEmbeddedMessagingManager?.()?.getMessages?.()
 */
function safeGetEmbeddedMessages (embeddedManager: unknown): unknown[] {
  const em = embeddedManager as { getMessages?: () => unknown } | null | undefined
  const fn = em?.getMessages
  if (typeof fn !== 'function') return []
  try {
    const raw = fn.call(em)
    return Array.isArray(raw) ? raw : []
  } catch (err) {
    logIterableError('safeGetEmbeddedMessages getMessages', err)
    return []
  }
}

// Types use `any` due to SDK constraints; replace with Iterable types when available.
interface IterablePluginProvide {
  setEmail: (email: string) => Promise<string>
  setUserID: (userId: string) => Promise<string>
  logout: () => void
  embeddedManager: InstanceType<any>
  sessionManager: InstanceType<any>
  embeddedMessages: Ref<any[]>
  embeddedMessagesByPlacement: Ref<Record<number, any[]>>
  syncEmbeddedMessages: (placementIds?: number[]) => Promise<unknown>
  startEmbeddedSession: () => void
  endEmbeddedSession: () => Promise<unknown>
  startImpression: (messageId: string, placementId: number) => void
  pauseImpression: (messageId: string) => void
  packageName: string
  /** True after first app mount Iterable init attempt finishes (success or failure). */
  identityReady: Ref<boolean>
  /** True once setEmail / setUserID ran with a non-empty value (incl. test env). */
  hasIterableIdentity: Ref<boolean>
  /** Clear debounced page:finish sync timer (call from app root onBeforeUnmount for SPA/HMR). */
  cleanupPageFinishDebounce: () => void
}

async function iterableClientPlugin (nuxtApp: NuxtApp) {
  let pageFinishSyncTimer: ReturnType<typeof setTimeout> | null = null

  const config = useRuntimeConfig()
  const iterableEnabled = Boolean(config.public.iterableEnabled)
  /** Injected at build time (`vite.define`); not stored in `runtimeConfig.public` / `window.__NUXT__`. */
  const webApiKey =
    typeof __ITERABLE_WEB_API_KEY__ === 'string' ? __ITERABLE_WEB_API_KEY__.trim() : ''
  const packageName = (config.public.iterablePackageName as string) || 'website'
  const pageFinishDebounceMs = Math.max(
    0,
    Number(config.public.iterablePageFinishSyncDebounceMs) || 400
  )
  const postRaw = Number(config.public.iterablePostSetEmailDelayMs)
  const postSetEmailDelayMs = Number.isFinite(postRaw) ? Math.max(0, postRaw) : 0

  if (!iterableEnabled || !webApiKey) {
    return {
      provide: {
        iterable: null
      }
    }
  }

  try {
    const sdk = await import('@iterable/web-sdk').catch((err) => {
      logIterableError('SDK load failed', err)
      return null
    })
    if (!sdk) {
      return { provide: { iterable: null } }
    }

    const {
      initializeWithConfig,
      IterableEmbeddedManager,
      IterableEmbeddedSessionManager,
      IterableConfig
    } = sdk

    const mailtoTelSmsScheme = /^(mailto|tel|sms):/i

    /** Cross-origin only: new tab — never same-tab assign (product: external stays in new window). */
    function openIterableExternalUrl (absoluteUrl: string, logContext: string): void {
      try {
        const win = window.open(absoluteUrl, '_blank', 'noopener,noreferrer')
        if (win == null && import.meta.dev) {
          console.debug(
            `[Iterable] ${logContext}: window.open returned null (e.g. popup blocked); URL not opened in same tab by design.`,
            absoluteUrl
          )
        }
      } catch (err) {
        logIterableError(logContext, err)
      }
    }

    function openIterableEmbeddedUrl (raw: string): void {
      if (mailtoTelSmsScheme.test(raw)) {
        try {
          window.location.href = raw
        } catch (err) {
          logIterableError('openIterableEmbeddedUrl mailto/tel/sms', err)
        }
        return
      }
      try {
        const parsedUrl = new URL(raw, window.location.href)
        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
          const isSameSite = parsedUrl.origin === window.location.origin
          try {
            if (isSameSite) {
              window.location.assign(raw)
            } else {
              openIterableExternalUrl(parsedUrl.href, 'openIterableEmbeddedUrl cross-origin')
            }
          } catch (err) {
            logIterableError('openIterableEmbeddedUrl navigate', err)
          }
          return
        }
      } catch (err) {
        logIterableError('openIterableEmbeddedUrl URL parse', err)
      }
      try {
        window.location.assign(raw)
      } catch (err) {
        logIterableError('openIterableEmbeddedUrl fallback assign', err)
      }
    }

    Object.assign(IterableConfig, {
      urlHandler: {
        handleIterableURL (uri: string, _ctx: unknown): boolean {
          const raw = uri?.trim() ?? ''
          if (!raw) {
            logIterableError('IterableConfig.urlHandler.handleIterableURL', 'empty or whitespace uri')
            return false
          }
          try {
            openIterableEmbeddedUrl(raw)
          } catch (err) {
            logIterableError('IterableConfig.urlHandler.handleIterableURL', err)
          }
          return true
        }
      },
      customActionHandler: {
        handleIterableCustomAction (
          action: { type?: string; data?: string },
          actionContext: unknown
        ): boolean {
          const typeStr = typeof action?.type === 'string' ? action.type.trim() : ''
          if (!typeStr) {
            logIterableError('IterableConfig.customActionHandler', 'empty action type')
            return false
          }
          try {
            window.dispatchEvent(
              new CustomEvent('iterable:embedded-custom-action', {
                bubbles: true,
                detail: {
                  type: typeStr,
                  identifier: typeStr.replace(/^action:\/\//i, ''),
                  data: action?.data ?? '',
                  actionContext
                }
              })
            )
          } catch (err) {
            logIterableError('IterableConfig.customActionHandler', err)
          }
          return true
        }
      }
    })

    const generateJWT = async (payload: { email?: string; userID?: string }) => {
      const { email, userID } = payload ?? {}
      if (!email && !userID) {
        throw new Error('Iterable JWT requires email or userID')
      }
      return await fetchIterableJwtToken({
        email: email || undefined,
        userID: userID || undefined
      })
    }

    const { setEmail, setUserID, logout } = initializeWithConfig({
      authToken: webApiKey,
      generateJWT,
      // Align with @iterable/web-sdk InitializeConfig; widened for TS (dynamic import).
      configOptions: {
        enableUnknownActivation: true,
        identityResolution: {
          mergeOnUnknownToKnown: true,
          replayOnVisitorToKnown: true
        }
      } as Record<string, unknown>
    })

    const embeddedManager = new IterableEmbeddedManager(packageName)
    const sessionManager = new IterableEmbeddedSessionManager(packageName)

    /** Shallow: Iterable SDK message objects are opaque — deep `ref` can break Vue reactivity / class normalization. */
    const embeddedMessages = shallowRef<any[]>([])
    const embeddedMessagesByPlacement = shallowRef<Record<number, any[]>>({})
    const identityReady = ref(false)
    const hasIterableIdentity = ref(false)

    async function wrappedSetEmail (email: string) {
      const setEmailResult = await setEmail(email)
      if (email?.trim()) {
        hasIterableIdentity.value = true
      }
      return setEmailResult
    }

    async function wrappedSetUserID (userId: string) {
      const setUserIdResult = await setUserID(userId)
      if (userId?.trim()) {
        hasIterableIdentity.value = true
      }
      return setUserIdResult
    }

    const updateHandler = {
      onMessagesUpdated () {
        try {
          const messages = safeGetEmbeddedMessages(embeddedManager)
          embeddedMessages.value = messages.map((m) =>
            m != null && typeof m === 'object' ? markRaw(m as object) : m
          )
          const byPlacement: Record<number, any[]> = {}
          messages.forEach((msg) => {
            if (msg == null || typeof msg !== 'object') return
            const messageRecord = msg as { metadata?: Record<string, unknown> }
            const meta = messageRecord.metadata
            const placementRaw =
              meta != null && typeof meta === 'object'
                ? meta.placementId ?? meta.placement_id
                : undefined
            const placementKey = sdkMetadataPlacementIdToMapKey(placementRaw)
            if (placementKey == null) return
            if (!byPlacement[placementKey]) byPlacement[placementKey] = []
            /** Iterable SDK messages are class instances; deep reactivity / attr patch can throw (ToPrimitive). */
            byPlacement[placementKey].push(markRaw(messageRecord))
          })
          /**
           * Defer ref write + `markRaw` the map so SDK trees never enter the same render tick as
           * `DynamicComponents` / Card–Programs (avoids `patchAttr` / ToPrimitive during class/attr patch).
           */
          const snapshot = markRaw(byPlacement)
          nextTick(() => {
            try {
              embeddedMessagesByPlacement.value = snapshot
            } catch (assignErr) {
              logIterableError('onMessagesUpdated (embeddedMessagesByPlacement assign)', assignErr)
            }
          })
        } catch (err) {
          logIterableError('onMessagesUpdated', err)
        }
      },
      onEmbeddedMessagingDisabled () {
        embeddedMessages.value = []
        embeddedMessagesByPlacement.value = {}
      }
    }

    try {
      embeddedManager.addUpdateListener(updateHandler)
    } catch (err) {
      logIterableError('embeddedManager.addUpdateListener', err)
    }

    function syncEmbeddedMessages (placementIds?: number[]) {
      try {
        if (!webApiKey) {
          return Promise.resolve()
        }
        /** Same guard as debounced `page:finish`: no sync until init finished (JWT path ready). */
        if (!identityReady.value) {
          return Promise.resolve()
        }
        /**
         * Without setEmail/setUserID, the SDK issues credentialess GETs (no api-key / Bearer / email
         * query) → Iterable 401 "No API key found on request". Only sync after identity exists.
         */
        if (!hasIterableIdentity.value) {
          return Promise.resolve()
        }
        if (placementIds?.length) {
          const validPlacementIds = placementIds.filter((id) => id != null && Number(id) > 0)
          if (validPlacementIds.length === 0) {
            return Promise.resolve()
          }
        }
        if (typeof embeddedManager?.syncMessages !== 'function') {
          return Promise.resolve()
        }
        return Promise.resolve(
          embeddedManager.syncMessages(packageName, () => {
            updateHandler.onMessagesUpdated()
          }, placementIds)
        ).catch((err) => {
          logIterableError('syncEmbeddedMessages', err)
        })
      } catch (err) {
        logIterableError('syncEmbeddedMessages (sync)', err)
        return Promise.resolve()
      }
    }

    function cleanupPageFinishDebounce () {
      if (pageFinishSyncTimer) {
        clearTimeout(pageFinishSyncTimer)
        pageFinishSyncTimer = null
      }
    }

    /**
     * If sync ran before JWT/identity finished, messages could stay empty once.
     * Re-sync whenever identity becomes truthy (deferred lead-gen, race with first sync).
     */
    watch(
      hasIterableIdentity,
      (ready) => {
        if (ready) {
          syncEmbeddedMessages().catch((err) =>
            logIterableError('syncEmbeddedMessages (watch hasIterableIdentity)', err)
          )
        }
      },
      { flush: 'post' }
    )

    function startEmbeddedSession () {
      try {
        sessionManager?.startSession?.()
      } catch (err) {
        logIterableError('startEmbeddedSession', err)
      }
    }

    function endEmbeddedSession () {
      try {
        return sessionManager?.endSession?.() ?? Promise.resolve()
      } catch (err) {
        logIterableError('endEmbeddedSession', err)
        return Promise.resolve()
      }
    }

    function startImpression (messageId: string, placementId: number) {
      if (!messageId?.trim()) return
      try {
        sessionManager?.startImpression?.(messageId, placementId)
      } catch (err) {
        logIterableError('startImpression', err)
      }
    }

    function pauseImpression (messageId: string) {
      if (!messageId?.trim()) return
      try {
        sessionManager?.pauseImpression?.(messageId)
      } catch (err) {
        logIterableError('pauseImpression', err)
      }
    }

    const testEmail = config.public.iterableTestEmail as string
    const testUserId = config.public.iterableTestUserId as string
    if (import.meta.client) {
      const debouncedSyncAfterNavigation = () => {
        cleanupPageFinishDebounce()
        pageFinishSyncTimer = setTimeout(() => {
          pageFinishSyncTimer = null
          /** Avoid calling the SDK before Iterable init finished (`app:mounted`). */
          if (!identityReady.value) return
          /** `syncEmbeddedMessages` also no-ops without identity; skip work for anonymous visitors. */
          if (!hasIterableIdentity.value) return
          syncEmbeddedMessages().catch((err) => logIterableError('syncEmbeddedMessages (page:finish)', err))
        }, pageFinishDebounceMs)
      }

      /** Full document unload: clear debounce timer + end embedded session (not fired on in-app SPA navigations). */
      window.addEventListener('beforeunload', () => {
        cleanupPageFinishDebounce()
        void endEmbeddedSession().catch((err) => logIterableError('endEmbeddedSession (beforeunload)', err))
      })

      /**
       * Nuxt has no stable app-level unmount hook; pagehide clears the debounce when leaving the document
       * (back/forward cache, tab close, navigation away). SPA in-app navigations rely on the next debounce clearing the prior timer.
       */
      window.addEventListener('pagehide', cleanupPageFinishDebounce, { passive: true })

      if (import.meta.hot) {
        import.meta.hot.dispose(() => {
          cleanupPageFinishDebounce()
        })
      }

      nuxtApp.hook('app:mounted', async () => {
        try {
          if (testEmail || testUserId) {
            if (testEmail) {
              await wrappedSetEmail(testEmail)
            } else if (testUserId) {
              await wrappedSetUserID(testUserId)
            }
          } else {
            const brochureStore = useBrochureRequest()
            const storedEmail = brochureStore?.email?.trim?.() || brochureStore?.email
            if (storedEmail) {
              await wrappedSetEmail(storedEmail)
              await syncEmbeddedMessages().catch((err) =>
                logIterableError('syncEmbeddedMessages (brochure store email)', err)
              )
              if (postSetEmailDelayMs > 0) {
                await sleepMs(postSetEmailDelayMs)
              }
            }
          }
          startEmbeddedSession()
          /** Sync is driven by watch(hasIterableIdentity) when identity becomes true + page:finish debounce. */
        } catch (err) {
          logIterableError('app:mounted Iterable init', err)
        } finally {
          identityReady.value = true
        }
      })
      nuxtApp.hook('page:finish', debouncedSyncAfterNavigation)
    }

    const iterable: IterablePluginProvide = {
      setEmail: wrappedSetEmail,
      setUserID: wrappedSetUserID,
      logout,
      embeddedManager,
      sessionManager,
      embeddedMessages,
      embeddedMessagesByPlacement,
      syncEmbeddedMessages,
      startEmbeddedSession,
      endEmbeddedSession,
      startImpression,
      pauseImpression,
      packageName,
      identityReady,
      hasIterableIdentity,
      cleanupPageFinishDebounce
    }

    return {
      provide: {
        iterable
      }
    }
  } catch (err) {
    logIterableError('plugin init', err)
    return {
      provide: {
        iterable: null
      }
    }
  }
}

// Nuxt plugin typing can circularly reference the callback; keep runtime behavior explicit.
export default defineNuxtPlugin(iterableClientPlugin as Parameters<typeof defineNuxtPlugin>[0])
